import type { FC, JSX } from "react";

import { AllowedPartners } from "@expert/expert-university";
import { Accordion, Group, Paper, ScrollArea, Stack, Text } from "@mantine/core";
import { IconClipboardList } from "@tabler/icons-react";

import { AccordionSection as Section } from "@/common-ui/accordion/AccordionSection";
import { UncheckedRadioIcon } from "@/common-ui/icons/sales";

import { isValidHttpUrl } from "../utils";
import { partnerContent } from "./content/sidebarPartnerContent";
import styles from "./Sidebar.module.css";

interface SidebarSectionProps {
    title: string;
    content: {
        sectionTitle: string;
        sectionGroups: {
            heading: string | JSX.Element;
            bulletPoints: string[];
        }[];
    }[];
}

const SidebarSections: FC<SidebarSectionProps> = ({ title, content }) => (
    <Paper
        display="flex"
        shadow="md"
        radius="md"
        w="100%"
        mb="1rem"
        style={{
            background: "var(--mantine-color-dark-5)",
            flexDirection: "column",
        }}
    >
        <Group align="center" gap={8} py="1rem" px="1.25rem" c={"var(--mantine-color-dark-2)"}>
            <IconClipboardList size={20} />
            <Text fz="1rem" fw={700} tt="uppercase">
                {title}
            </Text>
        </Group>
        <Accordion
            defaultValue="Call Checklist"
            chevronPosition="right"
            // Referencing a class here allows us to skip adding a bottom border to the last accordion item
            // I'm not sure why Mantine doesn't do this by default
            classNames={{ item: styles.accordionItem }}
            styles={{
                chevron: { color: "var(--mantine-color-success-4)", scale: 1.2 },
                control: { color: "var(--mantine-color-dark-2)", padding: "1rem 1.5rem" },
                content: { fontSize: "0.875rem", lineHeight: "1rem", padding: "0 1.5rem 1rem" },
                label: { fontSize: "1rem", lineHeight: "1.25rem", fontWeight: 700 },
            }}
        >
            {content.map((section) => (
                <Section title={section.sectionTitle} key={section.sectionTitle}>
                    <Accordion.Panel>
                        {section.sectionGroups.map((group) => (
                            <Stack pt="0.5rem" gap="5px" key={group.heading as string}>
                                <Text fz="0.9rem" lh="1.65rem" c="white" mb="0.5rem">
                                    {group.heading}
                                </Text>
                                <div style={{ marginBottom: "0.625rem" }}>
                                    {group.bulletPoints?.map((text) =>
                                        text ? (
                                            <Group
                                                gap="5px"
                                                wrap="nowrap"
                                                justify="top"
                                                align="top"
                                                px="0.25rem"
                                                py="0.5rem"
                                                key={text}
                                            >
                                                <div style={{ paddingTop: 4 }}>
                                                    <UncheckedRadioIcon />
                                                </div>
                                                <Text fz="0.875rem" lh="1.5rem" c="var(--mantine-color-dark-0)">
                                                    {isValidHttpUrl(text) ? (
                                                        <a href={text} target="_blank" rel="noopener noreferrer">
                                                            {text}
                                                        </a>
                                                    ) : (
                                                        text
                                                    )}
                                                </Text>
                                            </Group>
                                        ) : null,
                                    )}
                                </div>
                            </Stack>
                        ))}
                    </Accordion.Panel>
                </Section>
            ))}
        </Accordion>
    </Paper>
);

export const Sidebar = ({ partner }: { partner: AllowedPartners }) => (
    <ScrollArea
        offsetScrollbars
        scrollbarSize={8}
        styles={{
            viewport: { paddingRight: 16 },
        }}
        h="100%"
    >
        {partnerContent[partner]?.serveSolveContent.length > 0 && (
            <SidebarSections title="Serve & Solve" content={partnerContent[partner]?.serveSolveContent} />
        )}
        {partnerContent[partner]?.sellSmartOfferContent.length > 0 && (
            <SidebarSections title="Sell - Smart Offer" content={partnerContent[partner].sellSmartOfferContent} />
        )}
        {partnerContent[partner]?.sellClosingContent.length > 0 && (
            <SidebarSections title="Sell - Closing" content={partnerContent[partner].sellClosingContent} />
        )}
    </ScrollArea>
);

export default Sidebar;
